import api, { ApiResponse } from './api';

export type BreakdownKey = 'flows' | 'offers' | 'landing_pages';

export interface SourceFreshness {
  name: string;
  clicks: number;
  cost_covers_through: string | null;
  behind_minutes: number | null;
}

export interface DataFreshness {
  traffic_through: string | null;
  conversions_through: string | null;
  sources: SourceFreshness[];
}

export interface BreakdownEntry {
  id: string;
  name: string;
  visits: number;
  conversions: number;
}

export interface TrackingStatsRow {
  /** A combination's is a JSON array of its values. */
  group_key: string;
  name?: string;
  keys?: Record<string, string>;
  names?: Record<string, string>;
  /** On a single entity grouping. */
  notes?: string | null;
  visits: number;
  bot_visits: number;
  unique_visits: number;
  clicks: number;
  unique_clicks: number;
  conversions: number;
  revenue: number;
  cost: number;
  profit: number;
  roi: number;
  /** revenue / cost — 1.0 = breakeven. Same basis as Facebook ROAS. */
  roas: number;
  cr: number;
  ctr: number;
  cv_rate: number;
  rpl: number;
  cpl: number;
  epv?: number;
  cpv?: number;
  /** Only when comparing: the same metrics over the preceding period, and the swing. */
  profit_delta?: number;
  /** Per-conversion-type counts (ct_*), and every prev_* twin of the above. */
  [metric: string]: unknown;
}

/** Sums are numbers; the ratios derived from them are pre-rounded strings. */
export interface TrackingTotals {
  visits: number;
  bot_visits: number;
  unique_visits: number;
  clicks: number;
  unique_clicks: number;
  conversions: number;
  revenue: number;
  cost: number;
  profit: number;
  ctr: string;
  cv_rate: string;
  cr: string;
  epv: string;
  cpv: string;
  roi: string;
  roas: string;
  rpl: string;
  cpl: string;
  [ctColumn: string]: number | string;
}

export interface TrackingStats {
  rows: TrackingStatsRow[];
  total_row_count: number;
  totals: TrackingTotals;
  /** Present only when the report was asked to compare, and could. */
  previous_totals?: TrackingTotals;
  totals_profit_delta?: number;
  conversion_type_columns?: { name: string; field: string }[];
  breakdowns?: Record<string, unknown>;
}

// Mirrors the ClickHouse clicks table (the endpoint SELECTs *), plus the *_name fields the
// controller resolves via dictGet. The diagnostic columns — click_type, is_bot,
// ext_ad_id, cost, flow/traffic-source — matter as much as the identifiers.
export interface ClickRecord {
  click_id: string;
  workspace_id: string;
  ad_id: string | null;
  ad_set_id: string | null;
  campaign_id: string | null;
  ad_account_id: string | null;
  offer_id: string;
  network_id: string | null;
  landing_page_id: string | null;
  flow_id: string | null;
  flow_path_id: string | null;
  traffic_source_id: string | null;
  // Resolved names (dictGet) — present on the clicks endpoint's response.
  offer_name?: string;
  landing_page_name?: string;
  traffic_source_name?: string;
  external_id: string | null;
  ext_ad_id: string | null;
  cost: number | null;
  original_cost: number | null;
  original_cost_currency: string | null;
  /** 'direct' | 'lp_view' | 'lp_click' — the funnel stage this row represents. */
  click_type: string | null;
  /** Why the visit routed as it did: 'match' | 'lander_fallback' | 'group_fallback' | 'fallback' | 'fallback_over_cap' | 'admitted_over_limit' | ''. */
  route_reason?: string | null;
  /** JSON trace of the routing decision; only populated on non-trivial outcomes (30-day TTL). */
  decision_path?: string | null;
  destination_url: string;
  request_url: string | null;
  referer: string | null;
  ip: string | null;
  country: string | null;
  region: string | null;
  lang: string | null;
  device: string | null;
  os: string | null;
  browser: string | null;
  user_agent: string | null;
  is_bot: number;
  bot_reason: string | null;
  external_id_type: string | null;
  v1: string | null; v2: string | null; v3: string | null; v4: string | null; v5: string | null;
  v6: string | null; v7: string | null; v8: string | null; v9: string | null; v10: string | null;
  created_at: string;
}

export type PostbackStatus = 'reported' | 'partial' | 'failed' | 'unknown' | 'filtered' | 'disabled' | 'none' | '';

export interface ConversionRecord {
  id: string;
  click_id: string;
  workspace_id: string;
  ad_id: string | null;
  offer_id: string | null;
  network_id: string | null;
  landing_page_id: string | null;
  offer_name?: string;
  network_name?: string;
  landing_page_name?: string;
  external_id_type: string | null;
  country: string | null;
  transaction_id: string | null;
  revenue: number;
  currency: string;
  event_type: string;
  type?: string | null;
  cost?: number | null;
  traffic_source_id?: string | null;
  traffic_source_name?: string;
  flow_id?: string | null;
  flow_name?: string;
  external_id?: string | null;
  postback_url?: string | null;
  postback_ip?: string | null;
  v1: string | null;
  v2: string | null;
  v3: string | null;
  v4?: string | null;
  v5?: string | null;
  v6?: string | null;
  v7?: string | null;
  v8?: string | null;
  v9?: string | null;
  v10?: string | null;
  postback_status?: PostbackStatus;
  postback_counts?: Record<string, number>;
  created_at: string;
  click_created_at?: string | null; // timestamp of the originating click (for time-to-convert)
}

export interface OutboundPostbackRow {
  entry_index: number;
  postback_name: string;
  postback_type: string;
  status: string;
  out_url: string;
  http_status: number | null;
  error: string;
  response: string;
  /** Outbound body as sent. Empty past the column's 1-month TTL, and for url postbacks. */
  request: string;
  duration_ms: number | null;
  created_at: string;
}

export const trackingService = {
  getStats: async (params: {
    date_from?: string;
    date_to?: string;
    group_by?: string | string[];
    limit?: number;
    offset?: number;
    // Entity filters accept a single id or an array (multi-select -> IN)
    offer_id?: string | string[];
    network_id?: string | string[];
    traffic_source_id?: string | string[];
    ad_id?: string | string[];
    campaign_id?: string | string[];
    landing_page_id?: string | string[];
    flow_id?: string | string[];
    country?: string;
    external_id_type?: string;
    hour?: string;
    device?: string;
    os?: string;
    os_version?: string;
    language?: string;
    region?: string;
    referrer_domain?: string;
    tags?: string | string[]; // tag ids (array preferred; comma-joined string still accepted)
    sort_by?: string;
    sort_order?: string;
    timezone?: number;
    include_bots?: boolean;      // false/omitted = exclude known crawlers from metrics
    conv_time_basis?: 'conversion' | 'click'; // bucket/attribute conversions by their time (default) or their click's time
    // Custom variable / dynamic report filters (v1-v10, etc.)
    [key: string]: string | number | boolean | string[] | undefined;
    include_breakdowns?: boolean;
  }, opts?: { workspaceId?: string }): Promise<TrackingStats> => {
    // A query workspaceIds overrides the global interceptor's selected-workspace param,
    // letting a dashboard panel pin itself to one workspace.
    const config = opts?.workspaceId ? { params: { workspaceIds: opts.workspaceId } } : undefined;
    const response = await api.post<ApiResponse<TrackingStats>>('/tracking/stats', params, config);
    return response.data.data!;
  },

  /** Admin-only: one row per accessible workspace (grouped by workspace_id), spanning all
   *  workspaces regardless of the selected-workspace filter. */
  getWorkspaceOverview: async (params: { date_from: string; date_to: string; timezone: number }): Promise<TrackingStats> => {
    const response = await api.post<ApiResponse<TrackingStats>>('/tracking/workspace-overview', params);
    return response.data.data!;
  },

  getDrilldown: async (params: {
    limit?: number;
    offset?: number;
    parent: Record<string, string>;
    child_group_by: string | string[];
    date_from?: string;
    date_to?: string;
    sort_by?: string;
    sort_order?: string;
    timezone?: number;
    include_bots?: boolean;      // false/omitted = exclude known crawlers from metrics
    conv_time_basis?: 'conversion' | 'click';
  }): Promise<{ rows: TrackingStatsRow[]; total_row_count: number }> => {
    const response = await api.post<ApiResponse<{ rows: TrackingStatsRow[]; total_row_count: number }>>('/tracking/drilldown', params);
    return response.data.data || { rows: [], total_row_count: 0 };
  },

  // The server runs the body through the same buildWhere as the reports, so every
  // report filter works here too — that's what lets a report row deep-link to its
  // own clicks with the exact same slice applied.
  getClicks: async (params: {
    date_from?: string;
    date_to?: string;
    offer_id?: string | string[];
    network_id?: string | string[];
    traffic_source_id?: string | string[];
    flow_id?: string | string[];
    landing_page_id?: string | string[];
    ad_id?: string | string[];
    country?: string;
    region?: string;
    device?: string;
    os?: string;
    language?: string;
    referrer_domain?: string;
    external_id_type?: string;
    hour?: string;
    tags?: string | string[];
    timezone?: number;
    include_bots?: boolean;
    limit?: number;
    offset?: number;
    [key: string]: string | number | boolean | string[] | undefined;
  }): Promise<{ data: ClickRecord[]; total: number }> => {
    const response = await api.post<ApiResponse<ClickRecord[]> & { _meta?: { total: number } }>('/tracking/clicks', params);
    return { data: response.data.data || [], total: (response.data as any)._meta?.total || 0 };
  },

  getConversions: async (params: {
    date_from?: string;
    date_to?: string;
    offer_id?: string | string[];
    network_id?: string | string[];
    traffic_source_id?: string | string[];
    event_type?: string;
    tags?: string | string[]; // tag ids (array preferred; comma-joined string still accepted)
    /** The range is stated in this zone; the server shifts the bounds to UTC. */
    timezone?: number;
    limit?: number;
    offset?: number;
  }): Promise<{ data: ConversionRecord[]; total: number }> => {
    const response = await api.post<ApiResponse<ConversionRecord[]> & { _meta?: { total: number } }>('/tracking/conversions', params);
    return { data: response.data.data || [], total: (response.data as any)._meta?.total || 0 };
  },

  getConversionOutbound: async (conversionId: string): Promise<OutboundPostbackRow[]> => {
    const response = await api.post<ApiResponse<OutboundPostbackRow[]>>(
      '/tracking/conversions/outbound', { conversion_id: conversionId },
    );
    return response.data.data || [];
  },

  /** Lead-attribute distribution (age/home/insured/…) for a report slice. */
  getLeadBreakdown: async (params: Record<string, unknown>): Promise<{
    total_conversions: number;
    fields: Record<string, { value: string; conversions: number; revenue: number }[]>;
  }> => {
    const response = await api.post<ApiResponse<{
      total_conversions: number;
      fields: Record<string, { value: string; conversions: number; revenue: number }[]>;
    }>>('/tracking/lead-breakdown', params);
    return response.data.data!;
  },

  uploadConversions: async (lines: string): Promise<{ inserted: number; click_ids: string[] }> => {
    const response = await api.post<ApiResponse<{ inserted: number; click_ids: string[] }>>('/tracking/conversions/upload', { lines });
    return response.data.data!;
  },

  // Force-refresh an entity name dictionary so a rename shows in reports immediately.
  reloadDictionary: async (entity: string): Promise<void> => {
    await api.post('/tracking/reload-dictionary', { entity });
  },

  getFreshness: async (): Promise<DataFreshness> => {
    const response = await api.get<ApiResponse<DataFreshness>>('/tracking/freshness');
    return response.data.data!;
  },

  getUnmatchedPostbacks: async (params: {
    date_from?: string;
    date_to?: string;
    /** The range is stated in this zone; the server shifts the bounds to UTC. */
    timezone?: number;
    reason?: string;
    sender_ip?: string;
    order_by?: string;
    order?: 'asc' | 'desc';
    limit?: number;
    offset?: number;
  }): Promise<{ data: UnmatchedPostbackRecord[]; total: number }> => {
    const response = await api.post<ApiResponse<UnmatchedPostbackRecord[]> & { _meta?: { total: number } }>(
      '/tracking/unmatched-postbacks', params);
    return { data: response.data.data || [], total: response.data._meta?.total || 0 };
  },
};

export interface UnmatchedPostbackRecord {
  reason: string;
  sender_ip: string;
  referer: string;
  click_id: string;
  event_type: string;
  payout: number;
  currency: string;
  request_url: string;
  request_body: string;
  network_seq: string;
  network_name: string;
  created_at: string;
}
